/**
 * ACME Ltd — the reference case. Every figure below reconciles:
 *
 *   Evidence            2025 Financial Statements p.12  revenue (gross)  $3,840,000
 *                       less returns & allowances p.13                    ($40,000)
 *   Analysis            net revenue 2025                                $3,800,000
 *   Evidence            2025 Financial Statements p.14  EBITDA            $341,800
 *   Analysis            EBITDA margin 341,800 / 3,800,000                     9.0%
 *   Analysis            adjusted EBITDA 341,800 + 60,000 − 35,000         $366,800
 *   Analysis            DSCR 366,800 / 258,300                              1.42x
 *   Analysis            debt / EBITDA 792,000 / 341,800                      2.32x
 *
 * Keep it that way. A demo of traceability that does not tie out teaches the
 * opposite of the lesson.
 */
import { dollars } from "@/lib/format";
import type { Borrower, CaseRecord } from "@/lib/types";

const FS = { documentId: "doc-acme-fs-2025", documentName: "2025 Financial Statements" };
const BANK = { documentId: "doc-acme-bank", documentName: "Bank Statements — Jan to Jul 2026" };
const AR = { documentId: "doc-acme-ar", documentName: "AR Aging & Customer Concentration" };
const LOAN = { documentId: "doc-acme-loan", documentName: "Meridian Capital Loan Agreement" };

export const acmeBorrower: Borrower = {
  id: "acme",
  name: "ACME Ltd.",
  industry: "Industrial components manufacturing",
  relationshipSince: 2022,
  riskGrade: "B",
  totalExposure: dollars(1_200_000),
  headquarters: "Columbus, Ohio",
  employees: 84,
};

export const acmeCase: CaseRecord = {
  case: {
    id: "acme-2026-wc",
    reference: "CR-2026-0431",
    borrowerId: "acme",
    borrowerName: "ACME Ltd.",
    purpose: "Working Capital",
    requestedAmount: dollars(450_000),
    stage: "underwriting",
    riskGrade: "B",
    openedAt: "2026-08-11T09:15:00Z",
    owner: "Tom Giladi",
    summary: [
      "Revenue increased 18.8% year over year to $3.80M net, driven by two large industrial accounts won in Q1 2025.",
      "EBITDA margin declined from 14.0% to 9.0% over the same period. The decline is volume-driven rather than pricing: input costs rose faster than the contracted escalators.",
      "Customer concentration is the dominant risk. The top account is 37% of revenue on a contract that renews in March 2027.",
    ],
    risks: [
      {
        id: "risk-concentration",
        label: "37% customer concentration in a single account",
        trace: {
          claim: "37% customer concentration",
          derivation: [
            "Analysis · Financials",
            "Top-customer revenue $1,406,000 ÷ net revenue $3,800,000 = 37.0%",
          ],
          evidence: { ...AR, page: 3, section: "Customer revenue split", extractedValueId: "ev-acme-concentration" },
        },
      },
      {
        id: "risk-margin",
        label: "EBITDA margin deterioration 14% → 9%",
        trace: {
          claim: "EBITDA margin 14% → 9%",
          derivation: [
            "Analysis · Financials",
            "EBITDA calculation",
            "2024: $448,000 ÷ $3,200,000 = 14.0%",
            "2025: $341,800 ÷ $3,800,000 = 9.0%",
          ],
          evidence: { ...FS, page: 14, section: "Consolidated statement of operations", extractedValueId: "ev-acme-ebitda" },
        },
      },
      {
        id: "risk-leverage",
        label: "Existing debt increased 30% year over year",
        trace: {
          claim: "Debt increased 30% YoY",
          derivation: ["Analysis · Financials", "2024 $610,000 → 2025 $792,000 = +29.8%"],
          evidence: { ...FS, page: 16, section: "Note 8 — Long-term debt", extractedValueId: "ev-acme-debt" },
        },
      },
    ],
    strengths: [
      {
        id: "strength-deposits",
        label: "Stable monthly deposits across 19 months",
        trace: {
          claim: "Stable monthly deposits",
          derivation: ["Analysis · Cash Flow", "Monthly deposit variance 6.2% against a 15% policy threshold"],
          evidence: { ...BANK, section: "Deposit summary", extractedValueId: "ev-acme-deposits" },
        },
      },
      { id: "strength-history", label: "Five-year operating history, four years profitable" },
      {
        id: "strength-payments",
        label: "No returned payments or NSF events on record",
        trace: {
          claim: "No returned payments",
          derivation: ["Analysis · Cash Flow", "0 NSF events across 19 months of statements"],
          evidence: { ...BANK, section: "Returned items" },
        },
      },
    ],
    headlineMetrics: [
      {
        id: "m-revenue",
        label: "Revenue",
        value: "$3.80M",
        trace: {
          claim: "Net revenue $3.80M",
          derivation: ["Analysis · Financials", "Gross $3,840,000 less returns & allowances $40,000"],
          evidence: { ...FS, page: 12, section: "Revenue", extractedValueId: "ev-acme-revenue" },
        },
      },
      {
        id: "m-ebitda",
        label: "Adjusted EBITDA",
        value: "$367K",
        trace: {
          claim: "Adjusted EBITDA $367K",
          derivation: [
            "Analysis · Financials",
            "EBITDA bridge",
            "Reported $341,800 + owner compensation $60,000 − one-time revenue $35,000",
          ],
          evidence: { ...FS, page: 14, section: "Consolidated statement of operations", extractedValueId: "ev-acme-ebitda" },
        },
      },
      {
        id: "m-dscr",
        label: "DSCR",
        value: "1.42x",
        trace: {
          claim: "DSCR 1.42x",
          derivation: ["Analysis · Cash Flow", "Adjusted EBITDA $366,800 ÷ annual debt service $258,300"],
          evidence: { ...LOAN, page: 4, section: "Schedule A — Amortisation" },
        },
      },
      {
        id: "m-leverage",
        label: "Debt / EBITDA",
        value: "2.32x",
        trace: {
          claim: "Debt / EBITDA 2.32x",
          derivation: ["Analysis · Financials", "Total debt $792,000 ÷ reported EBITDA $341,800"],
          evidence: { ...FS, page: 16, section: "Note 8 — Long-term debt", extractedValueId: "ev-acme-debt" },
        },
      },
    ],
    openQuestions: [
      {
        id: "q-may",
        question: "Explain the May 2026 cash-flow decline",
        target: { tab: "analysis", anchor: "cash-flow" },
        resolved: false,
      },
      {
        id: "q-customer",
        question: "Verify the top customer contract and renewal date",
        target: { tab: "evidence", anchor: "doc-acme-ar" },
        resolved: false,
      },
      {
        id: "q-covenant",
        question: "Confirm the existing debt covenant package",
        target: { tab: "evidence", anchor: "doc-acme-loan" },
        resolved: false,
      },
    ],
  },

  evidence: [
    {
      id: "doc-acme-fs-2025",
      caseId: "acme-2026-wc",
      kind: "document",
      name: "2025 Financial Statements",
      provider: "Borrower upload · Reviewed by Whitfield & Co.",
      receivedAt: "2026-08-11T10:04:00Z",
      pages: 18,
      status: "received",
      extracted: [
        {
          id: "ev-acme-revenue",
          label: "Revenue (gross)",
          value: "$3,840,000",
          amount: dollars(3_840_000),
          confidence: 0.98,
          status: "verified",
          page: 12,
          section: "Revenue",
        },
        {
          id: "ev-acme-returns",
          label: "Returns & allowances",
          value: "$40,000",
          amount: dollars(40_000),
          confidence: 0.94,
          status: "verified",
          page: 13,
          section: "Note 2 — Revenue recognition",
          note: "Deducted to reach the $3.80M net revenue used throughout the analysis.",
        },
        {
          id: "ev-acme-ebitda",
          label: "EBITDA",
          value: "$341,800",
          amount: dollars(341_800),
          confidence: 0.96,
          status: "verified",
          page: 14,
          section: "Consolidated statement of operations",
        },
        {
          id: "ev-acme-debt",
          label: "Total debt",
          value: "$792,000",
          amount: dollars(792_000),
          confidence: 0.71,
          status: "flagged",
          page: 16,
          section: "Note 8 — Long-term debt",
          note: "Note 8 lists $792,000 but the covenant schedule on page 17 implies $810,000. Reconcile against the Meridian loan agreement before decisioning.",
        },
        {
          id: "ev-acme-owner-comp",
          label: "Owner compensation",
          value: "$185,000",
          amount: dollars(185_000),
          confidence: 0.89,
          status: "verified",
          page: 15,
          section: "Note 6 — Related party transactions",
        },
      ],
    },
    {
      id: "doc-acme-bank",
      caseId: "acme-2026-wc",
      kind: "banking",
      name: "Bank Statements — Jan to Jul 2026",
      provider: "Plaid · Huntington National Bank",
      receivedAt: "2026-08-24T13:52:00Z",
      status: "received",
      extracted: [
        {
          id: "ev-acme-deposits",
          label: "Average monthly deposits",
          value: "$318,400",
          amount: dollars(318_400),
          confidence: 0.99,
          status: "verified",
          section: "Deposit summary",
        },
        {
          id: "ev-acme-deposit-variance",
          label: "Deposit variance",
          value: "6.2%",
          confidence: 0.97,
          status: "verified",
          section: "Deposit summary",
          note: "Policy threshold is 15%.",
        },
        {
          id: "ev-acme-nsf",
          label: "Returned items (NSF)",
          value: "0",
          confidence: 1,
          status: "verified",
          section: "Returned items",
        },
        {
          id: "ev-acme-may",
          label: "May 2026 closing balance",
          value: "$96,200",
          amount: dollars(96_200),
          confidence: 0.99,
          status: "flagged",
          section: "Monthly balances",
          note: "62% below the trailing six-month average. Open question on the case.",
        },
      ],
    },
    {
      id: "doc-acme-ar",
      caseId: "acme-2026-wc",
      kind: "accounting",
      name: "AR Aging & Customer Concentration",
      provider: "QuickBooks Online · synced",
      receivedAt: "2026-08-12T08:30:00Z",
      pages: 6,
      status: "received",
      extracted: [
        {
          id: "ev-acme-concentration",
          label: "Top customer share of revenue",
          value: "37.0%",
          confidence: 0.93,
          status: "verified",
          page: 3,
          section: "Customer revenue split",
          note: "Northbrook Industrial — $1,406,000 of $3,800,000 net revenue.",
        },
        {
          id: "ev-acme-ar-90",
          label: "AR over 90 days",
          value: "$41,300",
          amount: dollars(41_300),
          confidence: 0.95,
          status: "verified",
          page: 2,
          section: "Aging summary",
        },
        {
          id: "ev-acme-contract",
          label: "Top customer contract expiry",
          value: "March 2027",
          confidence: 0.62,
          status: "unverified",
          page: 5,
          section: "Contract register",
          note: "Read from a summary table. The underlying contract has not been provided.",
        },
      ],
    },
    {
      id: "doc-acme-loan",
      caseId: "acme-2026-wc",
      kind: "document",
      name: "Meridian Capital Loan Agreement",
      provider: "Borrower upload",
      receivedAt: "2026-08-13T15:20:00Z",
      pages: 31,
      status: "received",
      extracted: [
        {
          id: "ev-acme-debt-service",
          label: "Annual debt service",
          value: "$258,300",
          amount: dollars(258_300),
          confidence: 0.97,
          status: "verified",
          page: 4,
          section: "Schedule A — Amortisation",
        },
        {
          id: "ev-acme-covenant",
          label: "Existing leverage covenant",
          value: "Max 3.00x debt / EBITDA",
          confidence: 0.58,
          status: "unverified",
          page: 22,
          section: "Section 7.2 — Financial covenants",
          note: "Amendment No. 2 is referenced but not attached. The operative figure may have been reset.",
        },
      ],
    },
    {
      id: "doc-acme-credit",
      caseId: "acme-2026-wc",
      kind: "external",
      name: "Experian Business Credit Report",
      provider: "Experian · pulled 21 Aug 2026",
      receivedAt: "2026-08-21T11:00:00Z",
      status: "received",
      extracted: [
        {
          id: "ev-acme-intelliscore",
          label: "Intelliscore Plus",
          value: "71 / 100",
          confidence: 1,
          status: "verified",
          section: "Score",
        },
        {
          id: "ev-acme-liens",
          label: "Liens and judgments",
          value: "None",
          confidence: 1,
          status: "verified",
          section: "Public records",
        },
        {
          id: "ev-acme-dbt",
          label: "Days beyond terms",
          value: "4 days",
          confidence: 1,
          status: "verified",
          section: "Trade payment history",
        },
      ],
    },
  ],

  analysis: {
    caseId: "acme-2026-wc",
    financials: {
      periods: ["2023", "2024", "2025", "LTM"],
      rows: [
        {
          id: "fin-revenue",
          label: "Net revenue",
          values: { "2023": "$2.81M", "2024": "$3.20M", "2025": "$3.80M", LTM: "$4.02M" },
          emphasis: true,
          trace: {
            claim: "Net revenue $3.80M in 2025",
            derivation: ["Gross revenue $3,840,000 less returns & allowances $40,000"],
            evidence: { ...FS, page: 12, section: "Revenue", extractedValueId: "ev-acme-revenue" },
          },
        },
        {
          id: "fin-growth",
          label: "Revenue growth",
          values: { "2023": "—", "2024": "13.9%", "2025": "18.8%", LTM: "16.3%" },
          trace: {
            claim: "Revenue growth 18.8% in 2025",
            derivation: ["$3,800,000 ÷ $3,200,000 − 1 = 18.75%"],
            evidence: { ...FS, page: 12, section: "Revenue", extractedValueId: "ev-acme-revenue" },
          },
        },
        {
          id: "fin-ebitda",
          label: "EBITDA (reported)",
          values: { "2023": "$410K", "2024": "$448K", "2025": "$342K", LTM: "$350K" },
          emphasis: true,
          trace: {
            claim: "Reported EBITDA $341,800 in 2025",
            derivation: ["EBITDA calculation"],
            evidence: { ...FS, page: 14, section: "Consolidated statement of operations", extractedValueId: "ev-acme-ebitda" },
          },
        },
        {
          id: "fin-margin",
          label: "EBITDA margin",
          values: { "2023": "14.6%", "2024": "14.0%", "2025": "9.0%", LTM: "8.7%" },
          trace: {
            claim: "EBITDA margin 9.0% in 2025",
            derivation: ["EBITDA calculation", "$341,800 ÷ $3,800,000 = 9.0%"],
            evidence: { ...FS, page: 14, section: "Consolidated statement of operations", extractedValueId: "ev-acme-ebitda" },
          },
        },
        {
          id: "fin-debt",
          label: "Total debt",
          values: { "2023": "$420K", "2024": "$610K", "2025": "$792K", LTM: "$810K" },
          trace: {
            claim: "Total debt $792,000 in 2025",
            derivation: ["Note 8 — Long-term debt"],
            evidence: { ...FS, page: 16, section: "Note 8 — Long-term debt", extractedValueId: "ev-acme-debt" },
          },
        },
        {
          id: "fin-leverage",
          label: "Debt / EBITDA",
          values: { "2023": "1.02x", "2024": "1.36x", "2025": "2.32x", LTM: "2.31x" },
          trace: {
            claim: "Debt / EBITDA 2.32x in 2025",
            derivation: ["$792,000 ÷ $341,800 = 2.32x"],
            evidence: { ...FS, page: 16, section: "Note 8 — Long-term debt", extractedValueId: "ev-acme-debt" },
          },
        },
      ],
    },
    bridge: {
      reportedLabel: "Reported EBITDA",
      reported: dollars(341_800),
      adjustments: [
        {
          id: "adj-owner-comp",
          label: "Owner compensation above market",
          direction: "add",
          amount: dollars(60_000),
          reason: "Founder salary is $185K against a $125K market rate for the role. Add back the $60K excess.",
          user: "Tom Giladi",
          at: "2026-08-24T13:41:00Z",
        },
        {
          id: "adj-one-time",
          label: "One-time revenue",
          direction: "subtract",
          amount: dollars(35_000),
          reason: "Settlement receipt from a discontinued supplier claim. Not recurring.",
          user: "Tom Giladi",
          at: "2026-08-24T13:41:00Z",
        },
      ],
    },
    models: [
      {
        id: "model-credit",
        name: "Credit Model",
        version: "v4",
        result: "B",
        tone: "neutral",
        ranAt: "2026-08-24T14:17:00Z",
        drivers: ["Leverage 2.32x", "DSCR 1.42x", "Five-year operating history", "Concentration 37%"],
      },
      {
        id: "model-cashflow",
        name: "Cash Flow Model",
        version: "v2",
        result: "B+",
        tone: "positive",
        ranAt: "2026-08-24T14:17:00Z",
        drivers: ["Deposit variance 6.2%", "Zero NSF events", "May 2026 trough"],
      },
      {
        id: "model-fraud",
        name: "Fraud Model",
        version: "v1",
        result: "Low",
        tone: "positive",
        ranAt: "2026-08-24T14:17:00Z",
        drivers: ["Bank data matched to accounting", "No entity-graph anomalies"],
      },
      {
        id: "model-industry",
        name: "Industry Model",
        version: "v3",
        result: "B-",
        tone: "negative",
        ranAt: "2026-08-24T14:17:00Z",
        drivers: ["Industrial components input-cost pressure", "Sector margin compression 2.1pp"],
      },
    ],
    cashFlow: [
      { month: "Feb 2026", inflow: dollars(324_100), outflow: dollars(298_700), closing: dollars(241_800) },
      { month: "Mar 2026", inflow: dollars(341_600), outflow: dollars(312_400), closing: dollars(271_000) },
      { month: "Apr 2026", inflow: dollars(302_900), outflow: dollars(318_200), closing: dollars(255_700) },
      {
        month: "May 2026",
        inflow: dollars(188_400),
        outflow: dollars(347_900),
        closing: dollars(96_200),
        note: "Northbrook Industrial paid 34 days late; a $142K receipt landed in June.",
      },
      { month: "Jun 2026", inflow: dollars(451_300), outflow: dollars(305_100), closing: dollars(242_400) },
      { month: "Jul 2026", inflow: dollars(329_800), outflow: dollars(301_600), closing: dollars(270_600) },
    ],
    forecast: [
      { id: "fc-revenue", label: "Revenue", base: "$4.15M", downside: "$3.42M", upside: "$4.60M" },
      { id: "fc-ebitda", label: "Adjusted EBITDA", base: "$392K", downside: "$268K", upside: "$471K" },
      { id: "fc-dscr", label: "DSCR", base: "1.38x", downside: "0.94x", upside: "1.66x" },
      { id: "fc-leverage", label: "Debt / EBITDA", base: "2.61x", downside: "3.82x", upside: "2.17x" },
    ],
    covenantHeadroom: [
      { id: "cov-dscr", label: "DSCR floor 1.20x", value: "1.42x · 18% headroom" },
      { id: "cov-leverage", label: "Leverage cap 3.00x", value: "2.32x · 23% headroom" },
      { id: "cov-downside", label: "Downside DSCR", value: "0.94x · breaches floor" },
    ],
  },

  decision: {
    caseId: "acme-2026-wc",
    kind: "approve_with_conditions",
    amount: dollars(380_000),
    termMonths: 30,
    pricing: "Prime + 6.2%",
    riskGrade: "B",
    confidence: "high",
    positives: [
      {
        id: "pos-growth",
        label: "Revenue growth 18.8% year over year",
        trace: {
          claim: "Revenue growth 18.8%",
          derivation: ["Analysis · Financials", "$3,800,000 ÷ $3,200,000 − 1 = 18.75%"],
          evidence: { ...FS, page: 12, section: "Revenue", extractedValueId: "ev-acme-revenue" },
        },
      },
      {
        id: "pos-repayment",
        label: "Stable repayment behaviour — zero NSF events in 19 months",
        trace: {
          claim: "Stable repayment behaviour",
          derivation: ["Analysis · Cash Flow", "0 returned items across 19 months of statements"],
          evidence: { ...BANK, section: "Returned items", extractedValueId: "ev-acme-nsf" },
        },
      },
      { id: "pos-history", label: "Five-year operating history, four years profitable" },
    ],
    negatives: [
      {
        id: "neg-concentration",
        label: "Customer concentration 37%",
        trace: {
          claim: "Customer concentration 37%",
          derivation: ["Analysis · Financials", "$1,406,000 ÷ $3,800,000 = 37.0%"],
          evidence: { ...AR, page: 3, section: "Customer revenue split", extractedValueId: "ev-acme-concentration" },
        },
      },
      {
        id: "neg-margin",
        label: "EBITDA margin 14% → 9%",
        trace: {
          claim: "EBITDA margin 14% → 9%",
          derivation: [
            "Analysis · Financials",
            "EBITDA calculation",
            "2024: $448,000 ÷ $3,200,000 = 14.0%",
            "2025: $341,800 ÷ $3,800,000 = 9.0%",
          ],
          evidence: { ...FS, page: 14, section: "Consolidated statement of operations", extractedValueId: "ev-acme-ebitda" },
        },
      },
      {
        id: "neg-leverage",
        label: "Increasing leverage — 1.36x → 2.32x",
        trace: {
          claim: "Increasing leverage",
          derivation: ["Analysis · Financials", "2024 1.36x → 2025 2.32x"],
          evidence: { ...FS, page: 16, section: "Note 8 — Long-term debt", extractedValueId: "ev-acme-debt" },
        },
      },
    ],
    conditions: [
      {
        id: "cond-pg",
        label: "Personal guarantee",
        selected: true,
        rationale: "Standard below $500K where leverage exceeds 2.0x.",
      },
      {
        id: "cond-reporting",
        label: "Monthly reporting",
        selected: true,
        rationale: "Margin trend needs to be observed monthly rather than at year end.",
      },
      {
        id: "cond-leverage",
        label: "Maximum leverage covenant 3.00x",
        selected: true,
        rationale: "Downside case reaches 3.82x. The covenant is the control.",
      },
      {
        id: "cond-collateral",
        label: "Additional collateral",
        selected: false,
        rationale: "Not proposed. AR quality is adequate and the facility is already secured.",
      },
    ],
    outcome: null,
  },

  activity: [
    {
      id: "act-acme-1",
      at: "2026-08-24T13:41:00Z",
      actor: "Tom Giladi",
      kind: "adjustment",
      title: "EBITDA adjusted",
      from: "$342K",
      to: "$367K",
      reason: "Owner compensation add-back $60K, one-time revenue removed $35K",
    },
    {
      id: "act-acme-2",
      at: "2026-08-24T13:52:00Z",
      actor: "Plaid",
      kind: "evidence",
      title: "Bank statements uploaded",
      detail: "Jan to Jul 2026 · Huntington National Bank",
    },
    {
      id: "act-acme-3",
      at: "2026-08-24T14:17:00Z",
      actor: "Credit Model v4",
      kind: "model",
      title: "Credit Model v4 completed",
      detail: "Risk Grade: B",
    },
    {
      id: "act-acme-4",
      at: "2026-08-24T14:22:00Z",
      actor: "Tom Giladi",
      kind: "decision",
      title: "Changed recommended amount",
      from: "$380K",
      to: "$400K",
      reason: "Reverted after review — see 14:31",
    },
    {
      id: "act-acme-5",
      at: "2026-08-24T14:31:00Z",
      actor: "Tom Giladi",
      kind: "decision",
      title: "Changed recommended amount",
      from: "$400K",
      to: "$380K",
      reason: "Downside DSCR of 0.94x does not support $400K",
    },
    {
      id: "act-acme-6",
      at: "2026-08-13T15:20:00Z",
      actor: "ACME Ltd.",
      kind: "evidence",
      title: "Meridian Capital loan agreement received",
      detail: "31 pages · Amendment No. 2 referenced but not attached",
    },
    {
      id: "act-acme-7",
      at: "2026-08-11T09:15:00Z",
      actor: "Tom Giladi",
      kind: "stage",
      title: "Case opened",
      detail: "Working Capital · $450,000 requested",
    },
  ],
};
